/** The topbar notification dropdown: polls, plays a sound for urgent items, toasts, marks read. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import notificationsApi, { Notification } from '@/services/notifications-api';
import { useMarkAsRead, notificationKeys } from './notifications-hooks';

/**
 * Configuration for topbar notifications
 */
const TOPBAR_CONFIG = {
  POLLING_INTERVAL: 30000, // 30 seconds
  MAX_DISPLAY: 5, // Show max 5 notifications in dropdown
  STALE_TIME: 15000, // 15 seconds
  SOUND_ENABLED: true, // Enable sound for urgent notifications
};

/**
 * Sound notification types that should trigger audio alert
 */
const URGENT_TYPES = ['overdue', 'fine_incurred', 'maintenance'];

/**
 * Hook for librarian topbar notifications with real-time updates
 * 
 * Features:
 * - Auto-polling every 30 seconds
 * - Toast notifications for new items
 * - Sound alerts for urgent notifications
 * - Mark as read functionality
 * - Limited to most recent 5 notifications
 * 
 * @example
 * const { 
 *   notifications, 
 *   unreadCount, 
 *   isLoading, 
 *   markAsRead,
 *   markAllAsRead 
 * } = useLibrarianTopbarNotifications();
 */
export const useLibrarianTopbarNotifications = (options: { socketConnected?: boolean } = {}) => {
  const { socketConnected = false } = options;
  const queryClient = useQueryClient();
  const { mutate: markNotificationAsRead } = useMarkAsRead();
  const [previousUnreadCount, setPreviousUnreadCount] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element for notifications
  useEffect(() => {
    if (TOPBAR_CONFIG.SOUND_ENABLED && !audioRef.current) {
      audioRef.current = new Audio('/notification-sound.mp3');
      audioRef.current.volume = 0.5;
    }
  }, []);

  // Fetch recent unread notifications for topbar
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...notificationKeys.unread(), 'topbar'],
    queryFn: async () => {
      const response = await notificationsApi.getUnreadNotifications(TOPBAR_CONFIG.MAX_DISPLAY);
      return response;
    },
    staleTime: TOPBAR_CONFIG.STALE_TIME,
    // The dashboard socket pushes the unread count every 30 s (see
    // use-real-time-dashboard.ts); poll only while it is disconnected, and never
    // from a background tab.
    refetchInterval: socketConnected ? false : TOPBAR_CONFIG.POLLING_INTERVAL,
    refetchIntervalInBackground: false,
  });

  const notifications = data?.results || [];
  const unreadCount = data?.unread_count || 0;

  // Detect new notifications and show toast + play sound
  useEffect(() => {
    if (isLoading || error) return;

    // Check if we have new notifications
    if (unreadCount > previousUnreadCount && previousUnreadCount > 0) {
      const newNotifications = notifications.slice(0, unreadCount - previousUnreadCount);

      // Show toast for each new notification
      newNotifications.forEach((notification) => {
        const isUrgent = URGENT_TYPES.includes(notification.notification_type);

        // Show toast based on severity
        const toastFunction = isUrgent
          ? toast.error
          : notification.severity === 'warning'
            ? toast.warning
            : toast.info;

        toastFunction(notification.title, {
          description: notification.message,
          duration: isUrgent ? 6000 : 4000,
        });

        // Play sound for urgent notifications
        if (isUrgent && TOPBAR_CONFIG.SOUND_ENABLED && audioRef.current) {
          audioRef.current.play().catch((err) => {
            console.debug('Audio play failed:', err);
          });
        }
      });
    }

    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount, notifications, isLoading, error]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = async (notificationId: number) => {
    markNotificationAsRead(notificationId, {
      onSuccess: () => {
        // Silently update the cache without showing a toast
        queryClient.invalidateQueries({ queryKey: notificationKeys.unread() });
      },
    });
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onMutate: async () => {
      // Optimistically update the cache
      await queryClient.cancelQueries({ queryKey: notificationKeys.unread() });

      const previousData = queryClient.getQueryData([...notificationKeys.unread(), 'topbar']);

      // Set all notifications to read
      queryClient.setQueryData([...notificationKeys.unread(), 'topbar'], (old: any) => ({
        ...old,
        results: [],
        unread_count: 0,
      }));

      return { previousData };
    },
    onSuccess: () => {
      toast.success('All notifications marked as read');
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([...notificationKeys.unread(), 'topbar'], context.previousData);
      }
      toast.error('Failed to mark all notifications as read');
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    refetch,
  };
};

/**
 * Hook for notification icon mapping
 * Maps notification types to Lucide icons for consistent UI
 */
export const getNotificationIconData = (type: string) => {
  const iconMap: Record<string, string> = {
    overdue: 'AlertTriangle',
    return_due: 'Clock',
    hold_available: 'CheckCircle',
    fine_incurred: 'DollarSign',
    fine_paid: 'CheckCircle',
    book_reserved: 'BookOpen',
    system: 'Bell',
    maintenance: 'Settings',
    other: 'Info',
  };

  const colorMap: Record<string, string> = {
    overdue: 'text-red-500',
    return_due: 'text-orange-500',
    hold_available: 'text-green-500',
    fine_incurred: 'text-red-600',
    fine_paid: 'text-green-600',
    book_reserved: 'text-blue-500',
    system: 'text-gray-500',
    maintenance: 'text-yellow-500',
    other: 'text-gray-400',
  };

  const bgColorMap: Record<string, string> = {
    overdue: 'bg-red-50 dark:bg-red-900/20',
    return_due: 'bg-orange-50 dark:bg-orange-900/20',
    hold_available: 'bg-green-50 dark:bg-green-900/20',
    fine_incurred: 'bg-red-50 dark:bg-red-900/20',
    fine_paid: 'bg-green-50 dark:bg-green-900/20',
    book_reserved: 'bg-blue-50 dark:bg-blue-900/20',
    system: 'bg-gray-50 dark:bg-gray-900/20',
    maintenance: 'bg-yellow-50 dark:bg-yellow-900/20',
    other: 'bg-gray-50 dark:bg-gray-900/20',
  };

  return {
    iconName: iconMap[type] || 'Bell',
    color: colorMap[type] || 'text-gray-500',
    bgColor: bgColorMap[type] || 'bg-gray-50 dark:bg-gray-900/20',
  };
};

/**
 * @deprecated Use getNotificationIconData instead
 */
export const useNotificationIcon = getNotificationIconData;
